import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Calculator } from "lucide-react";
import { Modal, Field } from "@/pages/Agenda";
import { brl } from "@/lib/format";
import { Link } from "react-router-dom";

export default function Servicos() {
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => setItems((await api.get("/services")).data);
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="services-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-[#27272A]">Serviços</h1>
          <p className="text-[#71717A] text-sm mt-1">{items.length} serviços cadastrados</p>
        </div>
        <button data-testid="btn-new-service" onClick={() => { setEditing(null); setShowModal(true); }} className="btn-primary-nc flex items-center gap-1 text-sm">
          <Plus size={16} /> Novo
        </button>
      </div>

      {items.length === 0 ? (
        <div className="card-nc p-8 text-center" data-testid="services-empty">
          <p className="text-[#71717A] mb-3">Cadastre seus serviços para começar a agendar.</p>
          <button onClick={() => setShowModal(true)} className="btn-primary-nc">Cadastrar serviço</button>
        </div>
      ) : (
        <div className="grid gap-2">
          {items.map(s => (
            <div key={s.id} data-testid={`service-${s.id}`} className="card-nc p-4 flex items-center gap-3">
              <div className="flex-1">
                <div className="font-semibold text-[#27272A]">{s.name}</div>
                <div className="text-xs text-[#71717A]">{s.duration_minutes} min · {s.category || "Sem categoria"}</div>
                {s.calculated_price && (
                  <div className="text-xs text-[#4F7A61] mt-1">Cenário calculado: {brl(s.calculated_price)}</div>
                )}
              </div>
              <div className="text-right">
                <div className="font-mono-brand font-bold text-[#27272A]">{brl(s.price)}</div>
                <Link to={`/app/precificacao?service=${s.id}`} data-testid={`calc-${s.id}`}
                  className="text-[10px] uppercase font-bold text-[#682F3E] flex items-center gap-1 justify-end mt-1">
                  <Calculator size={12} /> Calcular
                </Link>
              </div>
              <button data-testid={`edit-svc-${s.id}`} onClick={() => { setEditing(s); setShowModal(true); }}
                className="text-[#71717A] p-2"><Pencil size={16} /></button>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <ServiceModal initial={editing} onClose={() => setShowModal(false)}
          onSaved={async () => { setShowModal(false); await load(); toast.success("Serviço salvo!"); }} />
      )}
    </div>
  );
}

function ServiceModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: initial?.name || "", category: initial?.category || "",
    duration_minutes: initial?.duration_minutes || 60, price: initial?.price || 0,
    description: initial?.description || "", active: initial?.active ?? true,
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Informe o nome."); return; }
    setLoading(true);
    try {
      if (initial) await api.patch(`/services/${initial.id}`, form);
      else await api.post("/services", form);
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  const remove = async () => {
    if (!window.confirm("Excluir?")) return;
    await api.delete(`/services/${initial.id}`);
    onSaved();
  };

  return (
    <Modal onClose={onClose} title={initial ? "Editar serviço" : "Novo serviço"} testid="modal-service">
      <Field label="Nome*"><input data-testid="svc-name" value={form.name} onChange={e => set("name", e.target.value)} className="input-nc" /></Field>
      <Field label="Categoria"><input data-testid="svc-cat" value={form.category} onChange={e => set("category", e.target.value)} className="input-nc" placeholder="Ex: Alongamento" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Duração (min)"><input data-testid="svc-dur" type="number" value={form.duration_minutes} onChange={e => set("duration_minutes", Number(e.target.value))} className="input-nc" /></Field>
        <Field label="Preço (R$)"><input data-testid="svc-price" type="number" step="0.01" value={form.price} onChange={e => set("price", Number(e.target.value))} className="input-nc" /></Field>
      </div>
      <Field label="Descrição"><textarea data-testid="svc-desc" rows={2} value={form.description} onChange={e => set("description", e.target.value)} className="input-nc" /></Field>
      <div className="flex gap-2">
        {initial && <button onClick={remove} data-testid="svc-delete" className="text-[#B76E56] p-3 hover:bg-[#F9ECE8] rounded-xl"><Trash2 size={18} /></button>}
        <button data-testid="svc-save" onClick={save} disabled={loading} className="btn-primary-nc flex-1">
          {loading ? "Salvando…" : "Salvar"}
        </button>
      </div>
    </Modal>
  );
}
